import { Box, CardContent, Grid, makeStyles, Typography } from '@material-ui/core';
import { Link } from 'react-router-dom';

import ActiveAlarms from 'src/components/ui/activeAlarms';
import DataLabel from 'src/components/ui/card/DataLabel';
import Equipment from 'src/components/ui/Equipment';

import { PathBuilder } from 'src/routing';

import { EquipmentData } from 'src/models/equipment';
import EquipmentList from './equipmentList';

import { formatPortfolioEquipment } from 'src/helpers/equipment';
import StatusIndicator from 'src/components/ui/statusIndicator';

interface ComponentProps {
  address: string;
  endUserName?: string;
  equipment: EquipmentData[];
  highestActiveAlarmCount: number | undefined;
  installerName?: string;
  name: string;
  siteId: number;
  showEquipmentList: boolean;
  isOnline: boolean | null;
  siteFetchTime?: Date;
}

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    justifyContent: 'space-between',

    '& + &': {
      marginTop: 24,
    },
  },
  name: {
    fontSize: 20,
    fontWeight: 500,
    marginBottom: 8,
  },
  address: {
    fontSize: 14,

    [theme.breakpoints.down('sm')]: {
      breakWord: 'white-space',
    },
  },
  container: {
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
  },
  equipmentGrid: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(2, 1fr)',
    width: '100%',
    marginTop: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  alarmStatusContainer: {
    display: 'flex',
    height: 48,
    alignItems: 'center',
    position: 'absolute',
    right: 16,
    top: 16,
  },
  status: {
    marginLeft: 16,
  },
}));

const Site = ({
  address,
  isOnline,
  endUserName,
  equipment,
  highestActiveAlarmCount,
  installerName,
  name,
  siteId,
  showEquipmentList,
  siteFetchTime,
}: ComponentProps) => {
  const classes = useStyles();
  const formattedEquipment = formatPortfolioEquipment(equipment);
  return (
    <Box style={{ position: 'relative' }}>
      <Box className={classes.alarmStatusContainer}>
        {!!highestActiveAlarmCount && (
          <ActiveAlarms siteId={siteId} quantity={highestActiveAlarmCount} />
        )}
        <StatusIndicator
          isOnline={isOnline}
          siteFetchTime={siteFetchTime}
          className={classes.status}
        />
      </Box>
      <Link className={classes.container} to={PathBuilder.SITE(siteId)}>
        <CardContent>
          <Grid item className={classes.row}>
            <Grid item>
              <Typography className={classes.name}>{name}</Typography>
              <Typography className={classes.address}>{address}</Typography>
            </Grid>
          </Grid>
          <Grid item className={classes.row}>
            {installerName && <DataLabel label="Installer" value={installerName} />}
            {endUserName && <DataLabel label="End-User" value={endUserName} />}
          </Grid>
          <Grid item>
            <Box className={classes.equipmentGrid}>
              {formattedEquipment.map((item, index) => (
                <Equipment
                  key={`${item.model}-${index}`}
                  model={item.model}
                  type={item.type}
                  quantity={item.quantity}
                  image={item.image}
                />
              ))}
            </Box>
          </Grid>
          {showEquipmentList && equipment.length > 0 && <EquipmentList equipmentList={equipment} />}
        </CardContent>
      </Link>
    </Box>
  );
};

export default Site;
